import Quill from 'quill'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Embed = Quill.import('blots/embed') as any

export interface InlineSelectOptions {
  options: string[]
  selected?: string | undefined
  placeholder?: string | undefined
  id?: string | undefined
}

/**
 * Blot customizado para criar campos de seleção inline no texto
 * Sintaxe: {select:placeholder?Opção1|Opção2|Opção3}
 * Ou: {select:Opção1|Opção2|Opção3} (sem placeholder)
 */
class InlineSelect extends Embed {
  static blotName = 'inline-select'
  static tagName = 'SPAN'
  static className = 'ql-inline-select'

  static create(value: InlineSelectOptions) {
    const node = super.create() as HTMLElement
    node.setAttribute('contenteditable', 'false')
    node.classList.add('ql-inline-select')
    
    const select = document.createElement('select')
    select.className = 'ql-inline-select-dropdown'
    
    // Adicionar placeholder se existir
    if (value.placeholder) {
      const placeholderOption = document.createElement('option')
      placeholderOption.value = ''
      placeholderOption.textContent = value.placeholder
      placeholderOption.disabled = true
      placeholderOption.selected = !value.selected
      select.appendChild(placeholderOption)
    }
    
    // Adicionar opções
    value.options.forEach(opt => {
      const option = document.createElement('option')
      option.value = opt
      option.textContent = opt
      if (value.selected === opt) {
        option.selected = true
      }
      select.appendChild(option)
    })
    
    // Salvar dados no elemento para serialização
    node.setAttribute('data-options', JSON.stringify(value.options))
    node.setAttribute('data-selected', value.selected || '')
    node.setAttribute('data-placeholder', value.placeholder || '')
    if (value.id) {
      node.setAttribute('data-select-id', value.id)
    }
    
    // Evento de mudança
    select.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement
      node.setAttribute('data-selected', target.value)
      // Disparar evento customizado
      node.dispatchEvent(new CustomEvent('select-change', { 
        bubbles: true, 
        detail: { value: target.value, id: value.id } 
      }))
    })
    
    node.appendChild(select)
    return node
  }

  static value(node: HTMLElement): InlineSelectOptions {
    const optionsStr = node.getAttribute('data-options') || '[]'
    return {
      options: JSON.parse(optionsStr),
      selected: node.getAttribute('data-selected') || undefined,
      placeholder: node.getAttribute('data-placeholder') || undefined,
      id: node.getAttribute('data-select-id') || undefined,
    }
  }
}

// Registrar o blot
Quill.register(InlineSelect, true)

/**
 * Insere um select inline na posição atual do cursor
 */
export function insertInlineSelect(
  quill: Quill, 
  options: string[], 
  placeholder?: string,
  id?: string
) {
  const range = quill.getSelection(true)
  quill.insertEmbed(range.index, 'inline-select', {
    options,
    placeholder,
    id,
  })
  quill.setSelection(range.index + 1)
}

/**
 * Converte sintaxe markdown customizada para HTML de select
 * Sintaxe: {select:Placeholder?Opção1|Opção2|Opção3}
 * Ou: {select:Opção1|Opção2|Opção3}
 * Com valor selecionado: {select:Placeholder?Opção1|*Opção2|Opção3} (* marca o selecionado)
 */
export function parseSelectMarkdown(html: string): string {
  // Padrão para encontrar {select:...}
  const selectPattern = /\{select:([^}]+)\}/g
  
  return html.replace(selectPattern, (_, content: string) => {
    let placeholder = ''
    let optionsStr = content
    let selected = ''
    
    // Verificar se tem placeholder (separado por ?)
    if (content.includes('?')) {
      const parts = content.split('?')
      placeholder = parts[0]
      optionsStr = parts.slice(1).join('?')
    }
    
    // Parse das opções (separadas por |)
    const options = optionsStr.split('|').map(opt => {
      const trimmed = opt.trim()
      // Verificar se está marcado como selecionado (*)
      if (trimmed.startsWith('*')) {
        selected = trimmed.substring(1)
        return trimmed.substring(1)
      }
      return trimmed
    }).filter(opt => opt.length > 0)
    
    // Gerar o HTML do select
    const optionsJson = JSON.stringify(options)
    let selectHtml = `<span class="ql-inline-select" data-options='${optionsJson}' data-selected="${selected}" data-placeholder="${placeholder}" contenteditable="false">`
    selectHtml += `<select class="ql-inline-select-dropdown">`
    
    if (placeholder) {
      selectHtml += `<option value="" disabled ${!selected ? 'selected' : ''}>${placeholder}</option>`
    }
    
    options.forEach(opt => {
      const isSelected = opt === selected
      selectHtml += `<option value="${opt}"${isSelected ? ' selected' : ''}>${opt}</option>`
    })
    
    selectHtml += `</select></span>`
    
    return selectHtml
  })
}

/**
 * Converte HTML de select de volta para sintaxe markdown customizada
 */
export function selectToMarkdown(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  
  const selectElements = doc.querySelectorAll('.ql-inline-select')
  selectElements.forEach((element) => {
    const optionsStr = element.getAttribute('data-options') || '[]'
    const options = JSON.parse(optionsStr) as string[]
    const selected = element.getAttribute('data-selected') || ''
    const placeholder = element.getAttribute('data-placeholder') || ''
    
    // Marcar opção selecionada com *
    const optionsWithSelected = options.map(opt => 
      opt === selected ? `*${opt}` : opt
    )
    
    let markdown = '{select:'
    if (placeholder) {
      markdown += `${placeholder}?`
    }
    markdown += optionsWithSelected.join('|')
    markdown += '}'
    
    element.replaceWith(document.createTextNode(markdown))
  })
  
  return doc.body.innerHTML
}

/**
 * Inicializa handlers para selects inline no Quill
 */
export function initInlineSelectModule(quill: Quill, onSelectChange?: (detail: { value: string; id?: string }) => void) {
  const container = quill.container
  
  // Listener para mudanças nos selects
  container.addEventListener('select-change', ((e: CustomEvent) => {
    onSelectChange?.(e.detail)
    // Atualizar o conteúdo do Quill
    quill.update('user')
  }) as EventListener)
}

export { InlineSelect }
